import {
  type Json,
  matchJson,
  getString,
  getArray,
  getObject,
} from '@packages/utilities/json';
import {identity, mapArray} from '@packages/utilities/fp/utils';
import {map as mapValues} from '@packages/utilities/key-map';

/* Fixes corrupt Record expressions where `val` is missing by adding an empty
 * val (`val: {}`).
 *
 * At the time of writing this it is unknown what is corrupting Records.
 */
export const fixEmptyRecordExpressions = matchJson<Json>({
  // Only objects are valid expressions but we intentionally return the data
  // unchanged if we are not dealing with objects. In such a case something is
  // horribly broken and we don’t want to make things worse by manipulating
  // the data.
  Null: identity,
  Boolean: identity,
  Number: identity,
  String: identity,
  Array: identity,
  Object: (obj) => {
    const typeJson = obj.type;
    if (typeJson == null) return obj;
    const typeString = getString(typeJson);
    if (typeString == null) return obj;
    const valJson = obj.val;

    // The fix
    if (typeString === 'Record' && valJson == null)
      return {type: typeString, val: {}};

    if (valJson == null) return obj;
    const fixer = expressionFixers[typeString];
    if (!fixer) return obj;
    const val = fixer(valJson);
    if (val === valJson) return obj;
    return {type: typeString, val};
  },
});

const fixValues = mapValues(fixEmptyRecordExpressions);
const fixItems = mapArray(fixEmptyRecordExpressions);

const expressionFixers: {
  [k: string]: (json: Json) => Json;
} = {
  Boolean: identity,
  Number: identity,
  Text: identity,
  Enum: identity,
  Record: (json) => {
    const props = getObject(json);
    if (props == null) return json;
    return fixValues(props);
  },
  Select: (json) => {
    const sel = getObject(json);
    if (sel == null) return json;
    const {from, prop} = sel;
    if (from == null || prop == null) return json;
    const newFrom = fixEmptyRecordExpressions(from);
    if (newFrom === from) return json;
    return {from: newFrom, prop};
  },
  Union: (json) => {
    const union = getObject(json);
    if (union == null) return json;
    const {tag, value} = union;
    if (tag == null || value == null) return json;
    const newValue = fixEmptyRecordExpressions(value);
    if (newValue === value) return json;
    return {tag, value: newValue};
  },
  Case: (json) => {
    const caseObj = getObject(json);
    if (caseObj == null) return json;
    const {value, cases} = caseObj;
    if (value == null || cases == null) return json;
    const casesObj = getObject(cases);
    if (casesObj == null) return json;
    const newCases = fixValues(casesObj);
    const newValue = fixEmptyRecordExpressions(value);
    if (newCases === casesObj && newValue === value) return json;
    return {value: newValue, cases: newCases};
  },
  List: (json) => {
    const items = getArray(json);
    if (items == null) return json;
    return fixItems(items);
  },
  Element: (json) => {
    const el = getObject(json);
    if (el == null) return json;
    const {id, type, data, meta} = el;
    if (id == null || type == null || data == null) return json;
    const newData = fixEmptyRecordExpressions(data);
    if (newData === data) return json;

    const newEl: {[k: string]: Json} = {
      id,
      type,
      data: newData,
    };

    if (meta) newEl.meta = meta;
    return newEl;
  },
  Variable: identity,
  Call: (json) => {
    const call = getObject(json);
    if (call == null) return json;
    const {fun, arg} = call;
    if (fun == null || arg == null) return json;
    const newFun = fixEmptyRecordExpressions(fun);
    const newArg = fixEmptyRecordExpressions(arg);
    if (newFun === fun && newArg === arg) return json;
    return {fun: newFun, arg: newArg};
  },
  Function: (json) => {
    const fun = getObject(json);
    if (fun == null) return json;
    const {param, body} = fun;
    if (param == null || body == null) return json;
    const newBody = fixEmptyRecordExpressions(body);
    if (newBody === body) return json;
    return {param, body: newBody};
  },
  Literal: identity,
  Distinct: (json) => {
    const dist = getObject(json);
    if (dist == null) return json;
    const {name, value} = dist;
    if (name == null || value == null) return json;
    const newValue = fixEmptyRecordExpressions(value);
    if (newValue === value) return json;
    return {name, value: newValue};
  },
  Extend: (json) => {
    const ext = getObject(json);
    if (ext == null) return json;
    const {record, prop, value} = ext;
    if (record == null || prop == null || value == null) return json;
    const newRecord = fixEmptyRecordExpressions(record);
    const newValue = fixEmptyRecordExpressions(value);
    if (newRecord === record && newValue === value) return json;
    return {record: newRecord, prop, value: newValue};
  },
  Typed: (json) => {
    const typed = getObject(json);
    if (typed == null) return json;
    const {type, expr} = typed;
    if (type == null || expr == null) return json;
    const newExpr = fixEmptyRecordExpressions(expr);
    if (newExpr === expr) return json;
    return {type, expr: newExpr};
  },
  Hole: identity,
};
